import { Link } from "react-router";
import { RouteFactory } from "@app/routePaths";
import BTLogo from "@assets/bt-icon-white.svg";
import type { PulsePageProps } from "@features/pulse/utils/types";
import PulseDesktopCarousel from "@features/pulse/variant/others/components/desktop/PulseDesktopCarousel";
import PulseMobileCarousel from "@features/pulse/variant/others/components/mobile/PulseMobileCarousel";
import PulseCloseButton from "@features/pulse/variant/others/components/PulseCloseButton";
import { useWindowSize } from "@hooks/useWindowSize";
import { contactFormUrl } from "@pages/Article/components/ArticlePurchaseLink/constants";
import { useFeatureFlagVariantKey } from "posthog-js/react";

/**
 * PulseOthersLayout is a React component that renders the layout for the Pulse variant manual and autoplay.
 * It displays a carousel of articles and handles navigation between them.
 *
 */
const PulseOthersLayout = ({
  activeArticle,
  sortedPulseArticles,
  handleNavigateLeftCard,
  handleNavigateRightCard,
  offset,
}: PulsePageProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();

  const variant = useFeatureFlagVariantKey("pulse_version_1_2")?.toString();

  if (sortedPulseArticles.length === 0) return <></>;

  if (!isScreenLG) {
    return (
      <PulseMobileCarousel
        sortedPulseArticles={sortedPulseArticles}
        variant={variant}
        activeArticle={activeArticle}
        handleNavigateRightCard={handleNavigateRightCard}
        handleNavigateLeftCard={handleNavigateLeftCard}
      />
    );
  }

  return (
    <>
      <div className="flex justify-between px-6 py-2">
        <Link to={`${RouteFactory.home}?ref=pulse`} reloadDocument>
          <img src={BTLogo} width={200} height={18} alt="The Business Times" />
        </Link>

        <PulseCloseButton
          activeArticle={activeArticle}
          sortedPulseArticles={sortedPulseArticles}
          isDesktop
        />
      </div>

      <PulseDesktopCarousel
        sortedPulseArticles={sortedPulseArticles}
        variant={variant}
        activeArticle={activeArticle}
        handleNavigateRightCard={handleNavigateRightCard}
        handleNavigateLeftCard={handleNavigateLeftCard}
        offset={offset}
      />

      <div className="flex justify-between p-6 pt-18 font-poppins text-4xs font-light tracking-2% text-gray-550 lg:block">
        <div className="flex items-center justify-end">
          AI-generated summary.{" "}
          <a
            target="_blank"
            href={contactFormUrl}
            className="underline-offset-4 hover:underline"
            rel="noreferrer"
          >
            Feedback.
          </a>
        </div>
      </div>
    </>
  );
};

export default PulseOthersLayout;
